import { type InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { QueryKey } from '@/Config/api/queryKey';
import { Event } from '@/Domain/api/Conversation/dto/Event.dto';
import { Message, MessageFeedbackInput } from '@/Domain/api/Conversation/dto/Message.dto';
import { Paginated } from '@/Domain/api/Shared/dto/Paginated.dto';
import { EventTypeEnum } from '@/Domain/enum/EventType.enum';

type UseMessageFeedbackParams = {
  conversationId: string;
  messageId: string;
  onSuccess?: () => void;
};

type FeedbackRequestOptions = {
  onSuccess?: () => void;
};

type SubmitFeedbackOptions = FeedbackRequestOptions & {
  showSuccessToast?: boolean;
};

const updateMessageInCache = (
  queryClient: ReturnType<typeof useQueryClient>,
  conversationId: string,
  updatedMessage: Message,
) => {
  queryClient.setQueryData<InfiniteData<Paginated<Event[]>>>(
    ['conversations', conversationId, 'events'],
    (oldData) => {
      if (!oldData) return oldData;

      return {
        ...oldData,
        pages: oldData.pages.map((page) => ({
          ...page,
          items: page.items.map((event) => {
            if (event.event_type === EventTypeEnum.MESSAGE && event.data.id === updatedMessage.id) {
              return {
                ...event,
                data: updatedMessage,
              };
            }
            return event;
          }),
        })),
      };
    },
  );
};

export const useMessageFeedback = ({
  conversationId,
  messageId,
  onSuccess,
}: UseMessageFeedbackParams) => {
  const { t } = useTranslation('inbox_v2');
  const queryClient = useQueryClient();

  const createMutation = useMutation<
    Message,
    unknown,
    { conversationId: string; messageId: string } & MessageFeedbackInput
  >({
    mutationKey: [QueryKey.conversation.createMessageFeedback],
    onSuccess: (updatedMessage) => {
      updateMessageInCache(queryClient, conversationId, updatedMessage);
      onSuccess?.();
    },
    onError: () => {
      toast.error(t('message_feedback.toast.error'));
    },
  });

  const updateMutation = useMutation<
    Message,
    unknown,
    { conversationId: string; messageId: string } & MessageFeedbackInput
  >({
    mutationKey: [QueryKey.conversation.updateMessageFeedback],
    onSuccess: (updatedMessage) => {
      updateMessageInCache(queryClient, conversationId, updatedMessage);
      onSuccess?.();
    },
    onError: () => {
      toast.error(t('message_feedback.toast.error'));
    },
  });

  const deleteMutation = useMutation<void, unknown, { conversationId: string; messageId: string }>({
    mutationKey: [QueryKey.conversation.deleteMessageFeedback],
    onSuccess: () => {
      // Refetch to get updated message without feedback
      void queryClient.invalidateQueries({
        queryKey: ['conversations', conversationId, 'events'],
      });
      toast.success(t('message_feedback.toast.deleted'));
      onSuccess?.();
    },
    onError: () => {
      toast.error(t('message_feedback.toast.error'));
    },
  });

  const submitFeedback = (
    data: MessageFeedbackInput,
    hasExistingFeedback: boolean,
    { showSuccessToast = true, onSuccess: onSubmitted }: SubmitFeedbackOptions = {},
  ) => {
    const mutation = hasExistingFeedback ? updateMutation : createMutation;
    mutation.mutate(
      { conversationId, messageId, ...data },
      {
        onSuccess: () => {
          if (showSuccessToast) {
            toast.success(
              hasExistingFeedback
                ? t('message_feedback.toast.updated')
                : t('message_feedback.toast.success'),
            );
          }
          onSubmitted?.();
        },
      },
    );
  };

  const deleteFeedback = ({ onSuccess: onDeleted }: FeedbackRequestOptions = {}) => {
    deleteMutation.mutate({ conversationId, messageId }, { onSuccess: onDeleted });
  };

  return {
    submitFeedback,
    deleteFeedback,
    isSubmitting: createMutation.isPending || updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};
